import React, { useState, useMemo } from 'react'
import type { ResourceDayViewProps, CalendarEvent } from '../types'
import { AppointmentCard } from '../components/AppointmentCard'
import { TimeLabels } from '../components/TimeLabels'
import { CurrentTimeIndicator } from '../components/CurrentTimeIndicator'
import { isToday, getEventsForDay } from '../utils/time'
import {
  calculateOverlapLayout,
  getOverlapEventStyle,
  DEFAULT_SLOT_HEIGHT,
  generateTimeSlots,
} from '../utils'
import { formatDateWithDay } from '../utils/locale'

export const ResourceDayView: React.FC<ResourceDayViewProps> = ({
  date,
  events,
  resources,
  slotDuration,
  startHour,
  endHour,
  editable,
  locale,
  selectedEventIds = [],
  onEventClick,
  onSlotClick,
  onEventDrop,
}) => {
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null)

  const slotHeight = DEFAULT_SLOT_HEIGHT
  const totalSlots = ((endHour - startHour) * 60) / slotDuration
  const gridHeight = totalSlots * slotHeight
  const slots = generateTimeSlots(startHour, endHour, slotDuration)

  // Group events by resource
  const eventsByResource = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>()
    const dayEvents = getEventsForDay(events, date)

    resources.forEach((resource) => {
      const resourceEvents = dayEvents.filter(
        (event) => event.doctor.id === resource.id
      )
      map.set(resource.id, resourceEvents)
    })

    return map
  }, [events, resources, date])

  const handleDragStart = (event: CalendarEvent) => (e: React.DragEvent) => {
    setDraggedEvent(event)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDrop = (
    e: React.DragEvent,
    hour: number,
    minute: number,
    resourceId: string
  ) => {
    e.preventDefault()
    if (draggedEvent && editable) {
      const newStart = new Date(date)
      newStart.setHours(hour, minute, 0, 0)
      onEventDrop?.(draggedEvent, newStart, resourceId)
      setDraggedEvent(null)
    }
  }

  const handleSlotClick = (hour: number, minute: number, resourceId: string) => {
    if (editable) {
      onSlotClick?.(date, hour, minute, resourceId)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  return (
    <div className="calendar-resource-day-view">
      {/* Day Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: isToday(date) ? '#fef3c7' : '#f9fafb',
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: 600,
            color: '#111827',
            textTransform: 'capitalize',
          }}
        >
          {formatDateWithDay(date, locale)}
        </h3>
      </div>

      {/* Resource Headers */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
        }}
      >
        {/* Empty corner for time labels */}
        <div style={{ width: '60px', flexShrink: 0 }} />

        {/* Resource column headers */}
        {resources.map((resource) => {
          const resourceEvents = eventsByResource.get(resource.id) || []

          return (
            <div
              key={resource.id}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '12px 8px',
                borderLeft: '1px solid #e5e7eb',
                minWidth: '150px',
              }}
            >
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#111827',
                }}
              >
                {resource.title} {resource.name}
              </div>
              {resource.specialty && (
                <div
                  style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    marginTop: '2px',
                  }}
                >
                  {resource.specialty}
                </div>
              )}
              <div
                style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  marginTop: '4px',
                }}
              >
                {resourceEvents.length}{' '}
                {resourceEvents.length === 1 ? 'termin' : 'termina'}
              </div>
            </div>
          )
        })}
      </div>

      {/* Time Grid Container */}
      <div
        style={{
          display: 'flex',
          maxHeight: '600px',
          overflowY: 'auto',
          overflowX: 'auto',
        }}
      >
        {/* Time Labels */}
        <TimeLabels
          startHour={startHour}
          endHour={endHour}
          slotDuration={slotDuration}
          slotHeight={slotHeight}
        />

        {/* Resource Columns */}
        <div style={{ display: 'flex', flex: 1 }}>
          {resources.map((resource) => {
            const resourceEvents = eventsByResource.get(resource.id) || []
            const eventLayouts = useMemo(
              () =>
                calculateOverlapLayout(resourceEvents, startHour, slotDuration, slotHeight),
              [resourceEvents, startHour, slotDuration, slotHeight]
            )

            return (
              <div
                key={resource.id}
                style={{
                  flex: 1,
                  position: 'relative',
                  borderLeft: '1px solid #e5e7eb',
                  minHeight: gridHeight,
                  minWidth: '150px',
                }}
              >
                {/* Time Slots */}
                {slots.map((slot) => {
                  const isHourStart = slot.minute === 0

                  return (
                    <div
                      key={`${resource.id}-${slot.hour}-${slot.minute}`}
                      style={{
                        height: slotHeight,
                        borderBottom: isHourStart
                          ? '1px solid #d1d5db'
                          : '1px dashed #e5e7eb',
                        cursor: editable ? 'pointer' : 'default',
                        boxSizing: 'border-box',
                      }}
                      onClick={() => handleSlotClick(slot.hour, slot.minute, resource.id)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, slot.hour, slot.minute, resource.id)}
                      className="calendar-time-slot"
                    />
                  )
                })}

                {/* Events Overlay */}
                {eventLayouts.map((layout) => {
                  const style = getOverlapEventStyle(layout)
                  const isSelected = selectedEventIds.includes(layout.event.id)

                  return (
                    <div
                      key={layout.event.id}
                      style={{
                        position: 'absolute',
                        top: style.top + 1,
                        left: style.left,
                        width: style.width,
                        height: style.height - 2,
                        zIndex: isSelected ? 2 : 1,
                        paddingLeft: '4px',
                        paddingRight: layout.totalColumns > 1 ? '2px' : '4px',
                      }}
                      onClick={(e) => {
                        const modifiers = {
                          ctrl: e.ctrlKey || e.metaKey,
                          shift: e.shiftKey,
                        }
                        onEventClick?.(layout.event, modifiers)
                      }}
                    >
                      <AppointmentCard
                        event={layout.event}
                        locale={locale}
                        compact={style.height < 60}
                        draggable={editable}
                        selected={isSelected}
                        onDragStart={handleDragStart(layout.event)}
                      />
                    </div>
                  )
                })}

                {/* Current Time Indicator */}
                {isToday(date) && (
                  <CurrentTimeIndicator
                    startHour={startHour}
                    slotDuration={slotDuration}
                    slotHeight={slotHeight}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
